import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getFestivalTheme } from '../utils/festivalTheme';
import './Navbar.css';

const SERVICE_GROUPS = [
  {
    group: 'PCB & Hardware',
    items: [
      { label: 'ATE Board Design', slug: 'ate-board-design' },
      { label: 'Probe Card', slug: 'probe-card' },
      { label: 'HSD & HDI PCB Designs', slug: 'hsd-hdi-pcb-design' },
      { label: 'MLO/MLC Substrate Design', slug: 'mlo-mlc-substrate-design' },
      { label: 'EDA Conversion', slug: 'eda-conversion' },
      { label: 'RF Design', slug: 'rf-design' },
      { label: 'Library Development', slug: 'library-development' },
      { label: 'DFM / DFT Analysis', slug: 'dfm-dft-analysis' },
      { label: 'Procurement & Assembly', slug: 'procurement-assembly' },
      { label: 'Signal & Power Integrity', slug: 'signal-power-integrity' },
      { label: 'CAM Validation and Editing', slug: 'cam-validation-editing' },
    ],
  },
  {
    group: 'Mechanical Division',
    isMechanical: true,
    items: [
      { label: 'Semiconductor', hasSub: true, subKey: 'semiconductor' },
      { label: 'EMS Mobile Assembly', slug: 'ems-mobile-assembly' },
      { label: 'Automation Machine Building', slug: 'automation-machine-building' },
      { label: 'Wire Harness', hasSub: true, subKey: 'wireharness' },
      { label: 'High Precision Machine Shop', slug: 'high-precision-machine-shop' },
      { label: 'On Site Service Support (OSS)', slug: 'on-site-service-support' },
    ],
    subPanels: {
      semiconductor: {
        title: 'Semiconductor',
        items: [
          { label: 'Turn Key Solution Provider', slug: 'turn-key-solution' },
          { label: 'Wafer Cassette', slug: 'wafer-cassette' },
          { label: 'Pogo Tower', slug: 'pogo-tower' },
          { label: 'Interface', slug: 'interface' },
          { label: 'Hinge, Manipulator & Cart', slug: 'hinge-manipulator-cart' },
          { label: 'Test Head Chassis Design & Mfg', slug: 'test-head-chassis' },
        ],
      },
      wireharness: {
        title: 'Wire Harness',
        items: [
          { label: 'Semiconductor / EMS Industries', slug: 'semiconductor-ems' },
          { label: 'Automation / Machine Building', slug: 'automation-machine-building' },
          { label: 'Defense / Military', slug: 'defense-military' },
          { label: 'Aerospace', slug: 'aero-space' },
          { label: 'Medical', slug: 'medical' },
          { label: 'Telecom', slug: 'telecom' },
        ],
      },
    },
  },
  {
    group: 'Chip & ASIC',
    items: [
      { label: 'ASIC Design Flow', slug: 'asic-design-flow' },
      { label: 'Verification Services', slug: 'verification-services' },
      { label: '— SMT —', isSubheading: true },
      { label: 'Fully Automated Assembly', slug: 'fully-automated-assembly' },
      { label: '3-Line SMT Support', slug: 'three-line-smt-support' },
      { label: '90,000 CPH Capacity', slug: 'smt-cph-capacity' },
    ],
  },
  {
    group: 'IT Software Services',
    isIT: true,
    items: [
      { label: 'Web App Development', slug: 'web-app-development' },
      { label: 'Software Application Dev', slug: 'software-application-dev' },
      { label: 'Mobile App Development', slug: 'mobile-app-development' },
      { label: 'Cloud App Development', slug: 'cloud-app-development' },
      { label: 'Embedded Software Dev', slug: 'embedded-software-dev' },
    ],
  },
];
const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services', hasDropdown: true },
  { label: 'Industries', href: '/industries' },
  { label: 'Career', href: '/career' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const navigate = useNavigate();
  const festival = getFestivalTheme();
  const festivalOffset = festival.active ? 38 : 0;
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [flyoutOpen, setFlyoutOpen] = useState(null);
  const goToService = (slug) => {
    navigate('/services/detail', { state: { slug } });
    setDropOpen(false);
    setFlyoutOpen(null);
  };
  const activeSection = location.pathname.replace('/', '') || 'home';

  // Add this new useEffect:
  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && !e.target.closest('.navbar')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <nav
      className={`navbar${scrolled ? ' navbar--scrolled' : ''}${!isHome && !scrolled ? ' navbar--solid' : ''}`}
      style={{ top: festivalOffset }}
    >
      <div className="navbar-inner">

        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img
            src="/logo_nav.png" alt="DWB Tech" className="navbar-logo-img"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.nextSibling.style.display = 'flex'; }}
          />
          <div className="navbar-company-name">
          </div>
          <div className="navbar-logo-fallback" style={{ display: 'none' }}>
            <div className="logo-icon">D</div>
            <div>
              <div className="logo-name">DWB<span>Tech</span></div>
              <div className="logo-tagline">PCB EXCELLENCE</div>
            </div>
          </div>
        </Link>

        {/* Desktop Links */}
        <ul className="navbar-links">
          {NAV_LINKS.map(link => (
            <li key={link.label} ref={link.hasDropdown ? dropRef : null}
              className={link.hasDropdown ? 'navbar-item--has-drop' : ''}>

              {link.hasDropdown ? (
                <button
                  className={`navbar-link navbar-drop-trigger${activeSection === 'services' ? ' navbar-link--active' : ''}${dropOpen ? ' navbar-drop-trigger--open' : ''}`}
                  onClick={() => {
                    navigate('/services');   /* ← navigate on click */
                    setDropOpen(false);
                  }}
                  onMouseEnter={() => setDropOpen(true)}
                >
                  {link.label}
                  <svg
                    className="drop-chevron"
                    width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    onClick={(e) => {
                      e.stopPropagation();   /* chevron-only toggles dropdown */
                      setDropOpen(o => !o);
                    }}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              ) : (
                <Link to={link.href}
                  className={`navbar-link${activeSection === link.label.toLowerCase() ? ' navbar-link--active' : ''}`}>
                  {link.label}
                </Link>
              )}

              {link.hasDropdown && (
                <div
                  className={`nav-dropdown${dropOpen ? ' nav-dropdown--open' : ''}`}
                  onMouseLeave={() => { setDropOpen(false); setFlyoutOpen(null); }}
                >
                  <div className="nav-dropdown-inner">

                    {/* PCB & Hardware + Chip & ASIC */}
                    {/* PCB & Hardware then Mechanical then Chip & ASIC — in that order */}
                    {/* PCB & Hardware only */}
                    {SERVICE_GROUPS.filter(g => !g.isIT && !g.isMechanical && g.group === 'PCB & Hardware').map(grp => (
                      <div key={grp.group} className="nav-drop-col">
                        <div className="nav-drop-group">{grp.group}</div>
                        {grp.items.map(item =>
                          item.isSubheading ? (
                            <div key={item.label} className="nav-drop-smt-heading">SMT</div>
                          ) : (
                            <button key={item.label}
                              className="nav-drop-item"
                              onClick={() => goToService(item.slug)}>
                              <span className="nav-drop-dot" />{item.label}
                            </button>
                          )
                        )}
                      </div>
                    ))}

                    {/* Mechanical Division — left: main list, right: sub-panel */}
                    {SERVICE_GROUPS.filter(g => g.isMechanical).map(grp => (
                      <div key={grp.group} className="nav-drop-col nav-drop-col--mechanical">
                        <div className="nav-drop-group">{grp.group}</div>
                        <div className="mech-layout">

                          <div className="mech-main">
                            {grp.items.map(item =>
                              item.hasSub ? (
                                <React.Fragment key={item.label}>
                                  <div
                                    className={`nav-drop-item mech-item--hassub${flyoutOpen === item.subKey ? ' mech-item--active' : ''}`}
                                    onMouseEnter={() => setFlyoutOpen(item.subKey)}
                                  >
                                    <span className="nav-drop-dot" />
                                    {item.label}
                                    <span className="mech-arrow">›</span>
                                  </div>

                                  {/* Sub-panel renders IMMEDIATELY after its parent item */}
                                  {flyoutOpen === item.subKey && grp.subPanels[item.subKey] && (
                                    <div className="mech-sub">
                                      <div className="mech-sub-title">
                                        {grp.subPanels[item.subKey].title}
                                      </div>
                                      {grp.subPanels[item.subKey].items.map(sub => (
                                        <button key={sub.label} className="nav-drop-item"
                                          onClick={() => goToService(sub.slug)}>
                                          <span className="nav-drop-dot" />{sub.label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </React.Fragment>
                              ) : (
                                <button
                                  key={item.label}
                                  className="nav-drop-item"
                                  onMouseEnter={() => setFlyoutOpen(null)}
                                  onClick={() => goToService(item.slug)}
                                >
                                  <span className="nav-drop-dot" />{item.label}
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Chip & ASIC */}
                    {SERVICE_GROUPS.filter(g => !g.isIT && !g.isMechanical && g.group === 'Chip & ASIC').map(grp => (
                      <div key={grp.group} className="nav-drop-col">
                        <div className="nav-drop-group">{grp.group}</div>
                        {grp.items.map(item =>
                          item.isSubheading ? (
                            <div key={item.label} className="nav-drop-smt-heading">SMT</div>
                          ) : (
                            <button key={item.label}
                              className="nav-drop-item"
                              onClick={() => goToService(item.slug)}>
                              <span className="nav-drop-dot" />{item.label}
                            </button>
                          )
                        )}
                      </div>
                    ))}

                    {/* IT Software Services */}
                    <div className="nav-drop-col nav-drop-col--it">
                      <div className="nav-drop-group nav-drop-group--it">
                        IT Software Services
                      </div>
                      {SERVICE_GROUPS.find(g => g.isIT).items.map(item => (
                        <button key={item.label}
                          className="nav-drop-item nav-drop-item--it"
                          onClick={() => goToService(item.slug)}>
                          <span className="nav-drop-dot" />{item.label}
                        </button>
                      ))}
                    </div>

                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>


        <button className={`hamburger${menuOpen ? ' hamburger--open' : ''}`}
          onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(link => (
            link.hasDropdown ? (
              <React.Fragment key={link.label}>
                <Link to={link.href} className="mobile-link"
                  onClick={() => setMenuOpen(false)}>{link.label}</Link>
              </React.Fragment>
            ) : (
              <Link key={link.label} to={link.href} className="mobile-link"
                onClick={() => setMenuOpen(false)}>{link.label}</Link>
            )
          ))}
        </div>
      )}
    </nav>
  );
}